/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import {
  Mic, MicOff, Home, BookOpen,
  Target, BarChart2, Play, Settings, LogOut, Clock,
  Mail, Lock, User, Eye, EyeOff, ArrowRight, Volume2, VolumeX
} from "lucide-react";
import { createClient } from "@supabase/supabase-js";

// ── SUPABASE ────────────────────────────────────────────────────
// Replace these with your actual Supabase project values
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "https://placeholder.supabase.co";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "placeholder-key";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ── COLOUR TOKENS ──────────────────────────────────────────────
const C = {
  bg: "#F7F8FA",
  white: "#FFFFFF",
  navy: "#0F172A",
  blue: "#2563EB",
  blueLight: "#EFF6FF",
  purple: "#7C3AED",
  purpleLight: "#F5F3FF",
  green: "#059669",
  greenLight: "#ECFDF5",
  orange: "#EA580C",
  orangeLight: "#FFF7ED",
  teal: "#0891B2",
  tealLight: "#ECFEFF",
  border: "#E2E8F0",
  textMuted: "#64748B",
  textSub: "#94A3B8",
  red: "#DC2626",
};

// ── GLOBAL STYLES ──────────────────────────────────────────────
const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${C.bg}; font-family: 'Plus Jakarta Sans', system-ui, sans-serif; color: ${C.navy}; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 4px; }

  @keyframes pulse-ring {
    0%   { box-shadow: 0 0 0 0 rgba(37,99,235,0.4); }
    70%  { box-shadow: 0 0 0 18px rgba(37,99,235,0); }
    100% { box-shadow: 0 0 0 0 rgba(37,99,235,0); }
  }
  @keyframes wave {
    0%,100% { height: 8px; }
    50%      { height: 28px; }
  }
  @keyframes fadeIn {
    from { opacity:0; transform:translateY(10px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes speaking-pulse {
    0%,100% { opacity: 1; }
    50%      { opacity: 0.5; }
  }
  @keyframes slideUp {
    from { opacity:0; transform:translateY(40px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  .fade-in { animation: fadeIn 0.35s ease both; }
  .slide-up { animation: slideUp 0.45s cubic-bezier(0.16,1,0.3,1) both; }

  .app-shell { display: flex; height: 100vh; overflow: hidden; }
  .desktop-sidebar {
    width: 64px; background: ${C.white};
    border-right: 1px solid ${C.border};
    display: flex; flex-direction: column;
    align-items: center; padding: 20px 0; gap: 8px; flex-shrink: 0;
  }
  .main-content { flex: 1; display: flex; overflow: hidden; flex-direction: column; }
  .mobile-bottom-nav { display: none; }
  .mobile-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 24px 10px; background: ${C.white};
    border-bottom: 1px solid ${C.border}; flex-shrink: 0;
  }
  .desktop-panel-grid { flex: 1; overflow-y: auto; -webkit-overflow-scrolling: touch; }
  .panel-col { display: none; }
  .panel-col.active-mobile { display: flex; flex-direction: column; flex: 1; }

  input:focus { outline: none !important; }
  button:focus { outline: none; }

  .auth-input:focus {
    border-color: ${C.blue} !important;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.1) !important;
  }

  .btn-primary {
    background: ${C.blue}; color: white; border: none;
    border-radius: 12px; padding: 14px; font-size: 14px;
    font-weight: 700; cursor: pointer; width: 100%;
    font-family: 'Plus Jakarta Sans', sans-serif;
    transition: all 0.2s; display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .btn-primary:hover { background: #1d4ed8; transform: translateY(-1px); box-shadow: 0 8px 25px rgba(37,99,235,0.3); }
  .btn-primary:active { transform: translateY(0); }
  .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

  .btn-google {
    background: white; color: ${C.navy}; border: 1.5px solid ${C.border};
    border-radius: 12px; padding: 13px; font-size: 14px;
    font-weight: 600; cursor: pointer; width: 100%;
    font-family: 'Plus Jakarta Sans', sans-serif;
    transition: all 0.2s; display: flex; align-items: center; justify-content: center; gap: 10px;
  }
  .btn-google:hover { border-color: ${C.blue}; background: ${C.blueLight}; }

  @media (max-width: 768px) {
    .app-shell { flex-direction: column; height: 100dvh; }
    .desktop-sidebar { display: none; }
    .main-content { flex: 1; overflow: hidden; flex-direction: column; }
    .mobile-bottom-nav {
      display: flex; background: ${C.white};
      border-top: 1px solid ${C.border};
      padding: 8px 0 env(safe-area-inset-bottom, 8px);
      flex-shrink: 0; z-index: 100;
    }
  }
`;

const injectStyles = () => {
  if (!document.getElementById("vg-global")) {
    const s = document.createElement("style");
    s.id = "vg-global";
    s.textContent = globalCSS;
    document.head.appendChild(s);
  }
};
injectStyles();

// ── HELPERS ─────────────────────────────────────────────────────
const card = (extra = {}) => ({
  background: C.white, borderRadius: 16, padding: "16px",
  border: `1px solid ${C.border}`, ...extra,
});

// Get first name from full name
const getFirstName = (fullName) => {
  if (!fullName) return "there";
  return fullName.split(" ")[0];
};

// Get initials from full name
const getInitials = (fullName) => {
  if (!fullName) return "?";
  return fullName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
};

// ── WAVEFORM ────────────────────────────────────────────────────
function Waveform({ active }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, height: 36 }}>
      {[0.4, 0.7, 1, 0.6, 0.9, 0.5, 0.8, 0.4, 0.7, 1, 0.6].map((h, i) => (
        <div key={i} style={{
          width: 4, height: active ? `${h * 28 + 4}px` : "8px",
          borderRadius: 4,
          background: active ? `hsl(${220 + i * 5},80%,${50 + i * 2}%)` : C.border,
          animation: active ? `wave ${0.8 + i * 0.1}s ease-in-out infinite` : "none",
          animationDelay: `${i * 0.07}s`,
          transition: "height 0.3s ease",
        }} />
      ))}
    </div>
  );
}

// ── PROGRESS BAR ────────────────────────────────────────────────
function ProgressBar({ pct, color = C.blue }) {
  return (
    <div style={{ height: 6, background: C.bg, borderRadius: 999, overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 999, transition: "width 0.6s ease" }} />
    </div>
  );
}

// ── SCORE DIAL ──────────────────────────────────────────────────
function ScoreDial({ score = 8.7 }) {
  const r = 44, circ = 2 * Math.PI * r;
  const dash = (score / 10) * circ;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <svg width={110} height={110} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={55} cy={55} r={r} fill="none" stroke={C.border} strokeWidth={10} />
        <circle cx={55} cy={55} r={r} fill="none" stroke={C.green} strokeWidth={10}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
      </svg>
      <div style={{ position: "relative", marginTop: -88, textAlign: "center", marginBottom: 24 }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: C.navy }}>{score}</div>
        <div style={{ fontSize: 11, color: C.textMuted }}>/10</div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// AUTH PAGE — Login + Signup
// ══════════════════════════════════════════════════════════════
// eslint-disable-next-line no-unused-vars
function AuthPage({ onAuthSuccess }) {
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleEmailAuth = async () => {
    setError(""); setSuccess("");
    if (!email || !password) { setError("Please fill in all fields."); return; }
    if (mode === "signup" && !fullName.trim()) { setError("Please enter your full name."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }

    setLoading(true);
    try {
      if (mode === "signup") {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email, password,
          options: { data: { full_name: fullName.trim() } }
        });
        if (signUpError) throw signUpError;
        if (data?.user?.identities?.length === 0) {
          setError("An account with this email already exists. Please log in.");
        } else {
          setSuccess("Account created! Check your email to confirm, then log in.");
          setMode("login");
        }
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
        // onAuthSuccess will be triggered by the auth state listener in App
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setError("");
    const { error: googleError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin }
    });
    if (googleError) setError(googleError.message);
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: `linear-gradient(135deg, #EFF6FF 0%, #F5F3FF 50%, #ECFDF5 100%)`,
      padding: "24px",
      fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>
      {/* Background decoration */}
      <div style={{
        position: "fixed", top: -100, right: -100, width: 400, height: 400,
        borderRadius: "50%", background: "rgba(37,99,235,0.06)", pointerEvents: "none",
      }} />
      <div style={{
        position: "fixed", bottom: -100, left: -100, width: 350, height: 350,
        borderRadius: "50%", background: "rgba(124,58,237,0.06)", pointerEvents: "none",
      }} />

      <div className="slide-up" style={{
        background: C.white, borderRadius: 24, padding: "40px 36px",
        width: "100%", maxWidth: 420,
        boxShadow: "0 20px 60px rgba(15,23,42,0.08), 0 4px 16px rgba(15,23,42,0.04)",
        border: `1px solid ${C.border}`,
        position: "relative", zIndex: 1,
      }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, margin: "0 auto 12px",
            background: `linear-gradient(135deg, ${C.blue}, ${C.purple})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 24, fontWeight: 900, color: "white",
            boxShadow: "0 8px 24px rgba(37,99,235,0.3)",
          }}>V</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.navy }}>Vagdevi</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>
            Your AI English & Startup Coach
          </div>
        </div>

        {/* Tab switcher */}
        <div style={{
          display: "flex", background: C.bg, borderRadius: 12, padding: 4, marginBottom: 28,
        }}>
          {["login", "signup"].map(m => (
            <button key={m} onClick={() => { setMode(m); setError(""); setSuccess(""); }}
              style={{
                flex: 1, padding: "9px", borderRadius: 9, border: "none",
                background: mode === m ? C.white : "transparent",
                color: mode === m ? C.navy : C.textMuted,
                fontWeight: mode === m ? 700 : 500, fontSize: 13,
                cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif",
                boxShadow: mode === m ? "0 1px 4px rgba(15,23,42,0.08)" : "none",
                transition: "all 0.2s",
              }}>
              {m === "login" ? "Log In" : "Sign Up"}
            </button>
          ))}
        </div>

        {/* Fields */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {mode === "signup" && (
            <div style={{ position: "relative" }}>
              <User size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: C.textMuted }} />
              <input
                className="auth-input"
                type="text"
                placeholder="Full Name"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                style={{
                  width: "100%", padding: "13px 14px 13px 40px",
                  border: `1.5px solid ${C.border}`, borderRadius: 12,
                  fontSize: 14, fontFamily: "'Plus Jakarta Sans', sans-serif",
                  background: C.bg, color: C.navy, transition: "all 0.2s",
                }}
              />
            </div>
          )}

          <div style={{ position: "relative" }}>
            <Mail size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: C.textMuted }} />
            <input
              className="auth-input"
              type="email"
              placeholder="Email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleEmailAuth()}
              style={{
                width: "100%", padding: "13px 14px 13px 40px",
                border: `1.5px solid ${C.border}`, borderRadius: 12,
                fontSize: 14, fontFamily: "'Plus Jakarta Sans', sans-serif",
                background: C.bg, color: C.navy, transition: "all 0.2s",
              }}
            />
          </div>

          <div style={{ position: "relative" }}>
            <Lock size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: C.textMuted }} />
            <input
              className="auth-input"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleEmailAuth()}
              style={{
                width: "100%", padding: "13px 40px 13px 40px",
                border: `1.5px solid ${C.border}`, borderRadius: 12,
                fontSize: 14, fontFamily: "'Plus Jakarta Sans', sans-serif",
                background: C.bg, color: C.navy, transition: "all 0.2s",
              }}
            />
            <button onClick={() => setShowPassword(p => !p)} style={{
              position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
              background: "none", border: "none", cursor: "pointer", color: C.textMuted, padding: 0,
            }}>
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {/* Error / Success messages */}
          {error && (
            <div style={{
              background: "#FEF2F2", border: "1px solid #FECACA",
              borderRadius: 10, padding: "10px 14px",
              fontSize: 13, color: C.red, fontWeight: 500,
            }}>{error}</div>
          )}
          {success && (
            <div style={{
              background: C.greenLight, border: `1px solid ${C.green}33`,
              borderRadius: 10, padding: "10px 14px",
              fontSize: 13, color: C.green, fontWeight: 500,
            }}>{success}</div>
          )}

          <button className="btn-primary" onClick={handleEmailAuth} disabled={loading}
            style={{ marginTop: 4 }}>
            {loading ? "Please wait…" : mode === "login" ? <>Log In <ArrowRight size={16} /></> : <>Create Account <ArrowRight size={16} /></>}
          </button>

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "4px 0" }}>
            <div style={{ flex: 1, height: 1, background: C.border }} />
            <span style={{ fontSize: 12, color: C.textMuted, fontWeight: 500 }}>or</span>
            <div style={{ flex: 1, height: 1, background: C.border }} />
          </div>

          <button className="btn-google" onClick={handleGoogle}>
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
              <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.962L3.964 6.294C4.672 4.167 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>
        </div>

        <div style={{ textAlign: "center", marginTop: 20, fontSize: 12, color: C.textMuted }}>
          By continuing, you agree to Vagdevi's Terms of Service
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PANEL — HOME (Voice Call) — with all voice fixes
// ══════════════════════════════════════════════════════════════
function HomePanel({ user }) {
  const firstName = getFirstName(user?.user_metadata?.full_name || user?.email);
  const [messages, setMessages] = useState([
    { from: "ai", text: `Good morning, ${firstName} 👋 Ready to sharpen your communication skills today? Let's practice!` },
  ]);
  const [input, setInput] = useState("");
  const [recording, setRecording] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakingMuted, setSpeakingMuted] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const chatEndRef = useRef(null);
  const streamRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Pick best Indian English voice
  const getBestVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    // Priority order: Indian English > British English > any English
    const indiaVoice = voices.find(v => v.lang === "en-IN");
    const ukVoice = voices.find(v => v.lang === "en-GB");
    const usVoice = voices.find(v => v.lang === "en-US" && v.name.includes("Female"));
    const anyEnglish = voices.find(v => v.lang.startsWith("en"));
    return indiaVoice || ukVoice || usVoice || anyEnglish || null;
  };

  const speakReply = (text) => {
    if (speakingMuted) return;
    window.speechSynthesis.cancel(); // stop any current speech
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.92;
    utterance.pitch = 1.05;
    utterance.volume = 1;

    // Wait for voices to load if needed
    const setVoice = () => {
      const voice = getBestVoice();
      if (voice) utterance.voice = voice;
    };

    if (window.speechSynthesis.getVoices().length > 0) {
      setVoice();
    } else {
      window.speechSynthesis.onvoiceschanged = setVoice;
    }

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  const send = async () => {
    if (!input.trim()) return;
    const userMsg = { from: "user", text: input };
    setMessages(m => [...m, userMsg]);
    setInput("");
    setIsLoading(true);
    try {
      const res = await fetch("https://vagdevi-backend.vercel.app/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: input }),
      });
      const data = await res.json();
      setMessages(m => [...m, { from: "ai", text: data.reply }]);
      speakReply(data.reply);
    } catch {
      setMessages(m => [...m, { from: "ai", text: "Sorry, could not connect!" }]);
    }
    setIsLoading(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => { audioChunksRef.current.push(e.data); };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const formData = new FormData();
        formData.append("audio", audioBlob, "recording.webm");
        setIsLoading(true);
        setMessages(m => [...m, { from: "user", text: "🎤 Processing voice…" }]);
        try {
          // Use relative URL — works on both localhost and Vercel
          const res = await fetch("https://vagdevi-backend.vercel.app/api/voice", { method: "POST", body: formData });
          const data = await res.json();
          const transcript = data.transcript || "";
          const reply = data.reply || "";
          setMessages(m => [
            ...m.slice(0, -1),
            { from: "user", text: transcript },
            { from: "ai", text: reply },
          ]);
          speakReply(reply);
        } catch {
          setMessages(m => [...m, { from: "ai", text: "Sorry, voice processing failed!" }]);
        }
        setIsLoading(false);
        streamRef.current?.getTracks().forEach(t => t.stop());
      };

      mediaRecorder.start();
      setRecording(true);
    } catch {
      alert("Microphone access denied. Please allow microphone access!");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const getStatusText = () => {
    if (recording) return { text: "🔴 Recording… Speak now!", color: C.red };
    if (isLoading) return { text: "⏳ Processing your message…", color: C.orange };
    if (isSpeaking) return { text: "🔊 Veda is speaking…", color: C.blue };
    return { text: "✅ Ready to practice", color: C.green };
  };

  const status = getStatusText();

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "16px", gap: 14 }}>
      {/* Greeting */}
      <div style={{ ...card(), background: `linear-gradient(135deg, ${C.blue} 0%, ${C.purple} 100%)`, color: C.white, padding: "18px 20px" }}>
        <div style={{ fontSize: 13, opacity: 0.85 }}>Good morning</div>
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>{firstName} 👋</div>
        <div style={{ fontSize: 12, opacity: 0.8, background: "rgba(255,255,255,0.15)", borderRadius: 20, padding: "4px 10px", display: "inline-block" }}>
          Let's sharpen your communication today!
        </div>
      </div>

      {/* AI Coach Card */}
      <div style={{ ...card(), flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase" }}>AI Coach</div>

        {/* Avatar */}
        <div style={{
          width: 80, height: 80, borderRadius: "50%",
          background: `linear-gradient(135deg, ${C.blue}, ${C.purple})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          animation: isSpeaking ? "pulse-ring 1.5s infinite" : "none",
          fontSize: 32,
        }}>🤖</div>

        <div style={{ fontSize: 15, fontWeight: 700 }}>Veda — Your Coach</div>

        {/* Status indicator */}
        <div style={{
          fontSize: 12, color: status.color, fontWeight: 600,
          animation: isSpeaking ? "speaking-pulse 1.5s ease-in-out infinite" : "none",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          {status.text}
        </div>

        <Waveform active={recording || isSpeaking} />

        {/* Chat messages */}
        <div style={{
          width: "100%", maxHeight: 150, overflowY: "auto",
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          {messages.map((m, i) => (
            <div key={i} className="fade-in" style={{
              alignSelf: m.from === "ai" ? "flex-start" : "flex-end",
              background: m.from === "ai" ? C.blueLight : C.blue,
              color: m.from === "ai" ? C.navy : C.white,
              borderRadius: 12, padding: "8px 12px",
              fontSize: 12, maxWidth: "85%", lineHeight: 1.5,
            }}>{m.text}</div>
          ))}
          {isLoading && (
            <div style={{
              alignSelf: "flex-start", background: C.blueLight,
              borderRadius: 12, padding: "8px 12px", fontSize: 12,
            }}>
              <span style={{ letterSpacing: 2 }}>···</span>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Text input */}
        <div style={{ display: "flex", gap: 8, width: "100%" }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && send()}
            placeholder="Type your reply…"
            style={{
              flex: 1, border: `1px solid ${C.border}`, borderRadius: 24,
              padding: "10px 16px", fontSize: 13, outline: "none", background: C.bg,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          />
          <button onClick={send} style={{
            background: C.blue, color: C.white, border: "none", borderRadius: 24,
            padding: "10px 16px", cursor: "pointer", fontSize: 12, fontWeight: 700,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}>Send</button>
        </div>

        {/* Controls */}
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          {/* Mute AI voice */}
          <button
            onClick={() => { setSpeakingMuted(m => !m); if (!speakingMuted) stopSpeaking(); }}
            title={speakingMuted ? "Unmute AI voice" : "Mute AI voice"}
            style={{
              width: 48, height: 48, borderRadius: "50%", border: "none", cursor: "pointer",
              background: speakingMuted ? C.orange : C.bg,
              color: speakingMuted ? C.white : C.textMuted,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s",
            }}>
            {speakingMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>

          {/* Main mic button */}
          <button
            onClick={recording ? stopRecording : startRecording}
            disabled={isLoading}
            style={{
              width: 64, height: 64, borderRadius: "50%", border: "none", cursor: "pointer",
              background: recording ? C.red : C.green,
              color: C.white,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 4px 20px ${recording ? "rgba(220,38,38,0.4)" : "rgba(5,150,105,0.4)"}`,
              animation: recording ? "pulse-ring 1.5s infinite" : "none",
              transition: "background 0.2s",
            }}>
            {recording ? <MicOff size={24} /> : <Mic size={24} />}
          </button>

          {/* Stop speaking */}
          <button
            onClick={stopSpeaking}
            disabled={!isSpeaking}
            title="Stop AI speaking"
            style={{
              width: 48, height: 48, borderRadius: "50%", border: "none", cursor: isSpeaking ? "pointer" : "default",
              background: isSpeaking ? C.purpleLight : C.bg,
              color: isSpeaking ? C.purple : C.textSub,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s",
            }}>
            <VolumeX size={20} />
          </button>
        </div>

        <div style={{ fontSize: 11, color: C.textSub, textAlign: "center" }}>
          {recording ? "Tap the mic to stop recording" : "Tap the mic to start speaking"}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PANEL — LEARN
// ══════════════════════════════════════════════════════════════
const courses = [
  { title: "Pitch Perfecting", sub: "Investor Storytelling", pct: 72, time: "18 min", color: C.blue, icon: "🎯" },
  { title: "Negotiation Mastery", sub: "Win-Win Frameworks", pct: 45, time: "24 min", color: C.purple, icon: "🤝" },
  { title: "Executive Presence", sub: "Gravitas & Authority", pct: 28, time: "32 min", color: C.teal, icon: "👔" },
  { title: "Startup Vocabulary", sub: "VC & Product Lingo", pct: 90, time: "10 min", color: C.green, icon: "📚" },
];

function LearnPanel() {
  return (
    <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 20, fontWeight: 800 }}>Your Learning Path</div>
      <div style={{ fontSize: 13, color: C.textMuted }}>4 modules · 84 min total</div>
      {courses.map((c, i) => (
        <div key={i} style={{ ...card(), display: "flex", gap: 14, alignItems: "center" }} className="fade-in">
          <div style={{ width: 52, height: 52, borderRadius: 14, background: c.color + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>
            {c.icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{c.title}</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 8 }}>{c.sub}</div>
            <ProgressBar pct={c.pct} color={c.color} />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 11, color: C.textMuted }}>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Clock size={11} />{c.time}</span>
              <span style={{ fontWeight: 700, color: c.color }}>{c.pct}%</span>
            </div>
          </div>
          <button style={{ background: c.color, color: C.white, border: "none", borderRadius: 20, padding: "8px 14px", cursor: "pointer", fontSize: 12, fontWeight: 700, flexShrink: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Start
          </button>
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PANEL — PRACTICE
// ══════════════════════════════════════════════════════════════
const drills = [
  { title: "30-Second Elevator Pitch", sub: "Distill your startup to its essence", level: "Beginner", color: C.blue, icon: "🎙️" },
  { title: "VC Mock Q&A", sub: "Face tough investor questions", level: "Advanced", color: C.purple, icon: "💬" },
  { title: "Objection Handling", sub: "Turn pushback into opportunity", level: "Intermediate", color: C.green, icon: "⚡" },
  { title: "Storytelling Sprint", sub: "Craft a compelling narrative arc", level: "Intermediate", color: C.teal, icon: "📖" },
];
const levelColor = { Beginner: C.blue, Intermediate: C.green, Advanced: C.purple };

function PracticePanel() {
  return (
    <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 20, fontWeight: 800 }}>Practice</div>
      <div style={{ fontSize: 13, color: C.textMuted }}>Focused drills to sharpen specific skills</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {drills.map((d, i) => (
          <div key={i} style={{ ...card(), display: "flex", flexDirection: "column", gap: 10 }} className="fade-in">
            <div style={{ fontSize: 28 }}>{d.icon}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: levelColor[d.level], background: levelColor[d.level] + "18", borderRadius: 20, padding: "3px 8px", width: "fit-content" }}>
              {d.level}
            </div>
            <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.3 }}>{d.title}</div>
            <div style={{ fontSize: 11, color: C.textMuted, lineHeight: 1.4 }}>{d.sub}</div>
            <button style={{ background: d.color, color: C.white, border: "none", borderRadius: 20, padding: "10px", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              <Play size={12} /> Begin
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PANEL — STATS (with real user name)
// ══════════════════════════════════════════════════════════════
const weekDays = ["M", "T", "W", "T", "F", "S", "S"];
const weekActivity = [3, 7, 5, 8, 4, 2, 6];
const maxAct = Math.max(...weekActivity);

function StatsPanel({ user }) {
  const fullName = user?.user_metadata?.full_name || user?.email?.split("@")[0] || "User";
  const email = user?.email || "";
  const initials = getInitials(fullName);

  return (
    <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Profile — real user info */}
      <div style={{ ...card(), display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{
          width: 52, height: 52, borderRadius: "50%",
          background: `linear-gradient(135deg,${C.blue},${C.purple})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: C.white, fontWeight: 800, fontSize: 18, flexShrink: 0,
        }}>{initials}</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 16, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fullName}</div>
          <div style={{ fontSize: 12, color: C.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{email}</div>
        </div>
        <div style={{ marginLeft: "auto", background: C.blue, color: C.white, fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "4px 12px", flexShrink: 0 }}>✦ Member</div>
      </div>

      {/* Streak */}
      <div style={{ ...card({ background: C.orangeLight, border: `1px solid ${C.orange}22` }), display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ fontSize: 32 }}>🔥</div>
        <div>
          <div style={{ fontSize: 28, fontWeight: 800, color: C.orange }}>1</div>
          <div style={{ fontSize: 12, color: C.textMuted }}>Day Streak</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
          {[...Array(7)].map((_, i) => (
            <div key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: i < 1 ? C.orange : C.border }} />
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={card()}>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 6 }}>Total Practice Hours</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.navy }}>0</div>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>Just getting started!</div>
        </div>
        <div style={card()}>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 6 }}>Vocabulary Mastered</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.navy }}>0</div>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>Start learning!</div>
        </div>
      </div>

      {/* Weekly Activity */}
      <div style={card()}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Weekly Activity</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 60 }}>
          {weekActivity.map((v, i) => (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <div style={{
                width: "100%", borderRadius: 6,
                height: `${(v / maxAct) * 48}px`,
                background: i === new Date().getDay() - 1 ? C.blue : C.blueLight,
                transition: "height 0.5s ease",
              }} />
              <div style={{ fontSize: 10, color: C.textMuted }}>{weekDays[i]}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Score Dial */}
      <div style={{ ...card(), display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
        <div style={{ fontWeight: 700, fontSize: 14, alignSelf: "flex-start" }}>Communication Score</div>
        <ScoreDial score={0} />
        <div style={{ fontSize: 12, color: C.textMuted, marginTop: -16 }}>Complete sessions to build your score</div>
      </div>

      {/* Module Progress */}
      <div style={card()}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Module Progress</div>
        {courses.map((c, i) => (
          <div key={i} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
              <span style={{ fontWeight: 600 }}>{c.title}</span>
              <span style={{ color: c.color, fontWeight: 700 }}>0%</span>
            </div>
            <ProgressBar pct={0} color={c.color} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// DESKTOP SIDEBAR BUTTON
// ══════════════════════════════════════════════════════════════
function SideBtn({ icon: Icon, active, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      width: 44, height: 44, borderRadius: 12, border: "none", cursor: "pointer",
      background: active ? color + "18" : "transparent",
      color: active ? color : C.textSub,
      display: "flex", alignItems: "center", justifyContent: "center",
      transition: "all 0.2s",
    }}>
      <Icon size={20} />
    </button>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN APP
// ══════════════════════════════════════════════════════════════
const TABS = [
  { id: "home",     label: "Home",     Icon: Home,      color: C.blue   },
  { id: "learn",    label: "Learn",    Icon: BookOpen,  color: C.purple },
  { id: "practice", label: "Practice", Icon: Target,    color: C.green  },
  { id: "stats",    label: "Stats",    Icon: BarChart2, color: C.orange },
];

export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [active, setActive] = useState("home");

  // Listen for Supabase auth state
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  // Loading state
  if (authLoading) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: C.bg, fontFamily: "'Plus Jakarta Sans', sans-serif",
        flexDirection: "column", gap: 16,
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: 16,
          background: `linear-gradient(135deg, ${C.blue}, ${C.purple})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 24, fontWeight: 900, color: "white",
          animation: "pulse-ring 1.5s infinite",
        }}>V</div>
        <div style={{ fontSize: 14, color: C.textMuted }}>Loading Vagdevi…</div>
      </div>
    );
  }

  // Not logged in → show auth page
  ///if (!user) return <AuthPage onAuthSuccess={setUser} />;    if later login is needed then uncomment this and comment the line below
 
  // 🧪 TEMP: Mock user for testing — remove this when going live
if (!user) {
  setUser({
    email: "test@vagdevi.com",
    user_metadata: { full_name: "Shashank_Yapamakula" }
  });
  return null;
}

  const cur = TABS.find(t => t.id === active);
  const panelMap = {
    home:     <HomePanel user={user} />,
    learn:    <LearnPanel />,
    practice: <PracticePanel />,
    stats:    <StatsPanel user={user} />,
  };

  return (
    <div className="app-shell">
      {/* Desktop Sidebar */}
      <div className="desktop-sidebar">
        <div style={{ fontSize: 18, fontWeight: 900, color: C.blue, marginBottom: 16 }}>V</div>
        {TABS.map(t => (
          <SideBtn key={t.id} icon={t.Icon} active={active === t.id} color={t.color} onClick={() => setActive(t.id)} />
        ))}
        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
          <SideBtn icon={Settings} active={false} color={C.textMuted} onClick={() => {}} />
          <SideBtn icon={LogOut} active={false} color={C.textMuted} onClick={handleLogout} />
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Mobile Header */}
        <div className="mobile-header">
          <img src="/vagdevi-logo.svg" alt="Vagdevi" style={{ height: 32 }} />
          <div style={{ fontSize: 14, fontWeight: 700, color: cur.color }}>{cur.label}</div>
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            background: `linear-gradient(135deg,${C.blue},${C.purple})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: C.white, fontWeight: 800, fontSize: 12, cursor: "pointer",
          }} onClick={handleLogout} title="Log out">
            {getInitials(user?.user_metadata?.full_name || user?.email)}
          </div>
        </div>

        {/* Panels */}
        <div className="desktop-panel-grid">
          {TABS.map(t => (
            <div key={t.id} className={`panel-col ${active === t.id ? "active-mobile" : ""}`}>
              {panelMap[t.id]}
            </div>
          ))}
        </div>

        {/* Mobile Bottom Nav */}
        <div className="mobile-bottom-nav">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActive(t.id)} style={{
              flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", gap: 4, background: "none", border: "none",
              cursor: "pointer", padding: "6px 0",
              color: active === t.id ? t.color : C.textSub,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}>
              <t.Icon size={22} />
              <span style={{ fontSize: 10, fontWeight: active === t.id ? 700 : 500 }}>{t.label}</span>
              {active === t.id && (
                <div style={{ width: 4, height: 4, borderRadius: "50%", background: t.color }} />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}